import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Plus, X } from "lucide-react";
import { clientsApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * ClientCombobox
 *
 * Type-to-search combobox over the Client entity. When the search query
 * doesn't match any existing client, a "+ Create '{query}'" affordance
 * appears at the bottom of the dropdown — clicking it POSTs a new
 * client (name-only) and selects it.
 *
 * Value contract: parent stores `client_id` (or null) — the combobox
 * shows the resolved name in the trigger. `onChange({id, name})` fires
 * whenever the selection changes so the parent can also keep a legacy
 * `client` string field in sync if it wants to.
 *
 * Props:
 *   - value        : current client_id | null
 *   - onChange     : ({id, name} | {id: null, name: ""}) => void
 *   - disabled     : bool
 *   - testIdPrefix : string (defaults to "client-combobox")
 *   - initialName  : optional display fallback (for pre-fill from a
 *                    legacy project.client string that hasn't linked yet)
 */
export default function ClientCombobox({
  value,
  onChange,
  disabled = false,
  testIdPrefix = "client-combobox",
  initialName = "",
}) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Load client list once when the combobox first opens. We DON'T reload
  // on every keystroke — filtering is client-side against the cached list.
  useEffect(() => {
    if (!open || clients.length > 0 || loading) return;
    (async () => {
      setLoading(true);
      try {
        const list = await clientsApi.list();
        setClients(list);
      } catch {
        toast.error("Failed to load clients");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, clients.length, loading]);

  // Auto-focus the search input each time the panel opens.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 20);
    } else {
      setQuery("");
    }
  }, [open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === value) || null,
    [clients, value],
  );

  // Display name shown inside the trigger: prefer freshly-loaded client
  // record; if it hasn't loaded yet but we have an initialName, show it
  // so the trigger doesn't flash empty on dialog open.
  const displayName = selectedClient?.name || (value ? initialName : "");

  const normalized = (s) => (s || "").trim().toLowerCase();
  const filtered = useMemo(() => {
    const q = normalized(query);
    if (!q) return clients;
    return clients.filter((c) => normalized(c.name).includes(q));
  }, [clients, query]);

  const canCreate =
    query.trim().length > 0 &&
    !clients.some((c) => normalized(c.name) === normalized(query));

  const handleSelect = (c) => {
    onChange({ id: c.id, name: c.name });
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ id: null, name: "" });
    setQuery("");
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await clientsApi.create({ name });
      // Refresh cached list so it stays sorted and includes new entry
      setClients((prev) =>
        [...prev, created].sort((a, b) =>
          (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()),
        ),
      );
      onChange({ id: created.id, name: created.name });
      toast.success(`Client "${created.name}" created`);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create client");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        data-testid={`${testIdPrefix}-trigger`}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-zinc-950 border border-zinc-800 hover:border-zinc-700 focus:border-amber-500 focus:outline-none disabled:opacity-50"
      >
        <span
          data-testid={`${testIdPrefix}-value`}
          className={`flex-1 text-left truncate ${displayName ? "text-zinc-100" : "text-zinc-500"}`}
        >
          {displayName || "Select or add client…"}
        </span>
        {displayName && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            data-testid={`${testIdPrefix}-clear`}
            aria-label="Clear client"
            className="text-zinc-500 hover:text-rose-400"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronsUpDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
      </button>
      {open && (
        <div
          data-testid={`${testIdPrefix}-panel`}
          className="absolute z-50 top-full mt-1 left-0 right-0 bg-zinc-950 border border-zinc-800 rounded-md shadow-xl overflow-hidden"
        >
          <div className="p-2 border-b border-zinc-800">
            <input
              ref={inputRef}
              data-testid={`${testIdPrefix}-search`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filtered.length > 0) {
                    handleSelect(filtered[0]);
                  } else if (canCreate) {
                    handleCreate();
                  }
                }
              }}
              placeholder="Search clients or type a new name…"
              className="w-full bg-transparent text-sm text-zinc-100 placeholder:text-zinc-600 outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading && (
              <div className="p-3 flex items-center gap-2 text-xs text-zinc-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading clients…
              </div>
            )}
            {!loading && filtered.length === 0 && !canCreate && (
              <div className="p-3 text-xs text-zinc-500 italic">
                No clients yet. Type a name and press Enter to add.
              </div>
            )}
            {!loading &&
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`${testIdPrefix}-option-${c.id}`}
                  onClick={() => handleSelect(c)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-900"
                >
                  <span className="truncate">{c.name}</span>
                  {c.id === value && <Check className="w-3.5 h-3.5 text-amber-500" />}
                </button>
              ))}
            {!loading && canCreate && (
              <button
                type="button"
                data-testid={`${testIdPrefix}-create`}
                onClick={handleCreate}
                disabled={creating}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-400 hover:bg-amber-500/10 border-t border-zinc-800 disabled:opacity-50"
              >
                {creating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Create &ldquo;{query.trim()}&rdquo;
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
